/**
 * Generic confirm dialog for MDM commands. Spec §5.10.
 *
 * Duplicated from apps/web per spec §6.2. Keep in sync with the customer copy.
 *
 * Handles two `confirmLevel` modes:
 *   - `simple`      : Cancel + Confirm buttons
 *   - `typed-name`  : requires typing the translated label to enable Confirm
 *
 * Title comes from `t(cmd.i18nKey)`. Description falls back to "Are you
 * sure?" when `<i18nKey>.confirmDescription` is absent.
 */
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
} from '@staff-app/ui';
import type { MdmCommandDefinition } from '@staff-app/shared-types';

interface Props {
  /** When non-null, the dialog is open for this command. */
  cmd: MdmCommandDefinition | null;
  onConfirm: () => void;
  onClose: () => void;
}

export function MdmConfirmDialog({ cmd, onConfirm, onClose }: Props) {
  const [typed, setTyped] = useState('');

  // Reset typed state whenever the dialog opens for a new command.
  useEffect(() => {
    if (cmd) setTyped('');
  }, [cmd?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cmd) return null;

  // Catalog `i18nKey` values are flat English labels (e.g. "Reboot device").
  // With i18n dropped we use them verbatim.
  const label = cmd.i18nKey;
  const needsTyping = cmd.confirmLevel === 'typed-name';
  const canConfirm = !needsTyping || typed.trim() === label;
  const description = 'Are you sure?';

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent data-testid="mdm-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{label}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {needsTyping && (
          <div className="space-y-2">
            <Label htmlFor="mdm-confirm-typed-input">{`Type ${label} to confirm`}</Label>
            <Input
              id="mdm-confirm-typed-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              aria-label={'Typed confirmation'}
              autoFocus
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>{'Cancel'}</AlertDialogCancel>
          <Button
            variant={cmd.destructive ? 'destructive' : 'default'}
            disabled={!canConfirm}
            onClick={onConfirm}
            data-testid="mdm-confirm-dialog-confirm"
          >
            {label}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
